import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { UserType } from '@renovationman/ts-utils';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface AuthorizationSession {
  /**
   * La session est-elle réellement ouverte côté serveur ?
   *
   * <strong>Pourquoi ce drapeau existe.</strong> `resumeUri` nul dit deux choses opposées : soit la
   * session est ouverte et personne n'attendait de flux — le cas nominal d'une connexion venue
   * d'elle-même — soit l'appel s'est planté et il n'y a aucune session du tout. Remettre la personne
   * à son espace <em>sans</em> jeton suppose une session que l'espace retrouvera : seul ce drapeau
   * le garantit.
   */
  opened: boolean;

  /**
   * Chemin relatif où reprendre un flux d'autorisation interrompu, ou `null` quand l'utilisateur
   * est venu se connecter de lui-même. Toujours validé par ce service avant d'être rendu.
   */
  resumeUri: string | null;

  /**
   * Le type de l'utilisateur qui vient de s'authentifier — story O2.12. C'est sur lui que se choisit
   * l'espace quand aucun flux n'attendait ; le portail le lisait auparavant dans un jeton du login
   * historique, demandé à seule fin de le décoder. `null` si la session n'est pas ouverte.
   */
  userType: UserType | null;

  /**
   * L'`errorCode` du corps d'erreur, quand il y en a un — `INCORRECT_CREDENTIALS` pour un mauvais
   * mot de passe. `null` sur un succès, et sur une panne dont le corps n'est pas le JSON de l'API
   * (le HTML du behavior S3 par défaut, par exemple).
   */
  errorCode: string | null;
}

/**
 * La session du serveur d'autorisation — celle que `/oauth2/authorize` exige pour délivrer un code.
 * <p>
 * <strong>Pourquoi cet appel n'utilise pas l'api-client généré.</strong> Ce client est configuré une
 * fois pour toutes avec `environment.apiBasePaths.uaa`, soit le domaine du service — une **autre
 * origine** que celle de cette page. Le cookie de session est posé *sans* attribut `Domain`, donc il
 * est lié à l'hôte qui le pose : passer par le client généré le poserait sur `uaa-service`, et il ne
 * reviendrait jamais sur `/oauth2/authorize` d'ici. La configuration CORS partagée
 * (`addAllowedOrigin("*")` sans `allowCredentials`) l'interdirait de toute façon.
 * <p>
 * L'URL est donc **relative**, et c'est CloudFront qui la sert depuis UAA — le behavior posé en
 * story O2.3. En local, c'est `proxy.conf.json` qui joue ce rôle.
 * <p>
 * Les autres appels du portail ne doivent surtout pas suivre : `/password_manager/*` n'est
 * délibérément pas routé par le CDN et garde le chemin absolu.
 * <p>
 * Conséquence de cette URL relative : `ApiErrorInterceptor`, qui ne voit que les URL absolues, ne
 * voit pas ses erreurs. C'est l'appelant qui les affiche, d'après `errorCode`.
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationSessionService {
  private static readonly SESSIONS_PATH = '/auth_manager/sessions';

  constructor(private http: HttpClient) {}

  /**
   * Ouvre la session, et ne lève jamais.
   * <p>
   * Un mauvais mot de passe, un 404, un 502 ou le HTML du behavior S3 par défaut rendent tous
   * `opened: false`, avec l'`errorCode` du corps quand il y en a un : l'appelant n'a qu'une forme de
   * réponse à traiter, et c'est lui qui décide du message. Il n'y a plus de chemin de repli derrière
   * — c'est l'unique appel qui authentifie, depuis que le login historique est retiré (O2.12).
   * <p>
   * Le succès HTTP est la seule preuve qu'on ait que la session existe, et c'en est une bonne : le
   * serveur ne rend 200 qu'après avoir authentifié, appelé `changeSessionId()` et sauvegardé le
   * contexte de sécurité. Voir `SessionController.openSession`.
   */
  open(username: string, password: string): Observable<AuthorizationSession> {
    return this.http
      .post<{ resumeUri?: string | null; userType?: UserType | null }>(
        AuthorizationSessionService.SESSIONS_PATH,
        { username, password }
      )
      .pipe(
        map((session) => ({
          opened: true,
          resumeUri: AuthorizationSessionService.safeResumeUri(
            session?.resumeUri
          ),
          userType: session?.userType ?? null,
          errorCode: null,
        })),
        catchError((error: unknown) =>
          of({
            opened: false,
            resumeUri: null,
            userType: null,
            errorCode: AuthorizationSessionService.errorCodeOf(error),
          })
        )
      );
  }

  /**
   * Ferme la session côté serveur. Effacer le jeton dans le navigateur ne déconnecte de rien : la
   * session SSO redélivrerait un code à la première autorisation suivante, donc reconnecterait
   * silencieusement le précédent utilisateur sur un poste partagé.
   */
  close(): Observable<void> {
    return this.http
      .delete<void>(AuthorizationSessionService.SESSIONS_PATH)
      .pipe(catchError(() => of(undefined)));
  }

  /**
   * Le serveur rend un chemin relatif par construction — il ne recopie pas d'URL fournie par
   * l'appelant. On le vérifie quand même : la valeur part dans `window.location.assign`, et trois
   * lignes ici ferment définitivement la porte à une redirection ouverte. `//evil.com` est refusé
   * autant que `https://evil.com` : un chemin protocol-relative change d'hôte.
   */
  private static safeResumeUri(resumeUri?: string | null): string | null {
    if (
      !resumeUri ||
      !resumeUri.startsWith('/') ||
      resumeUri.startsWith('//')
    ) {
      return null;
    }
    return resumeUri;
  }

  /** HttpClient ne parse le corps que s'il est déclaré JSON : du HTML reste une chaîne, sans code. */
  private static errorCodeOf(error: unknown): string | null {
    const body = error instanceof HttpErrorResponse ? error.error : null;
    const errorCode =
      body && typeof body === 'object'
        ? (body as { errorCode?: unknown }).errorCode
        : null;
    return typeof errorCode === 'string' ? errorCode : null;
  }
}
